"use client";

import React, { useState, useEffect } from "react";
import { createStorefrontApiClient } from "@shopify/storefront-api-client";
import { toast } from "sonner";
import { useTranslation } from "next-i18next";
import { Card, CardContent, CardFooter } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { ButtonMaster } from "@/components/Atoms/Buttons";
import { MultiPurposeParagraph } from "@/components/Atoms/Paragraph";
import { trackAddToCart } from "@/lib/ga4-ecommerce";
import styles from "./index.module.scss";

interface ShopifySimpleBuyButtonProps {
  domain: string;
  storefrontAccessToken: string;
  productId?: string;
  productHandle?: string;
  variantId?: string;
  maxQuantity?: number;
  label?: string;
  color?: any;
  cta?: string;
  type?: any;
  customColor?: any;
  config: any;
  cart: any;
  disableQuantity?: boolean;
  /** e.g. `shopify-buy-typography` for Text Gallery buy CTAs only (silkspice tailwind) */
  cardFooterClassName?: string;
  /** When label or accent hex matches this surface, swap label ↔ accent on the buy button */
  surfaceBackgroundHex?: string;
  /** Brand identifier used as `item_brand` in the GA4 `add_to_cart` dataLayer event */
  brand?: string;
}

function normalizeHex(hex?: string | null): string | null {
  if (hex == null || typeof hex !== "string") return null;
  const h = hex.trim().replace(/^#/, "").toLowerCase();
  if (h.length === 3) {
    return h
      .split("")
      .map((c) => c + c)
      .join("");
  }
  return h.length === 6 || h.length === 8 ? h : null;
}

function resolveHex(value: any): string | null {
  if (value == null) return null;
  if (typeof value === "string") return normalizeHex(value);
  if (typeof value === "object" && typeof value.hex === "string")
    return normalizeHex(value.hex);
  return null;
}

function shouldSwapBuyButtonForSurface(
  surfaceHex: string | undefined,
  labelColor: any,
  accentColor: any
): boolean {
  const bg = normalizeHex(surfaceHex);
  if (!bg) return false;
  const label = resolveHex(labelColor);
  const accent = resolveHex(accentColor);
  return Boolean((label && label === bg) || (accent && accent === bg));
}

export default function ShopifySimpleBuyButton({
  domain,
  storefrontAccessToken,
  productId,
  productHandle,
  variantId,
  maxQuantity = 5,
  label,
  color,
  cta,
  type,
  customColor,
  config,
  cart,
  disableQuantity = false,
  cardFooterClassName,
  surfaceBackgroundHex,
  brand,
}: ShopifySimpleBuyButtonProps) {
  const { t } = useTranslation();
  const [product, setProduct] = useState<any>(null);
  const [selectedVariant, setSelectedVariant] = useState<any>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isAddingToCart, setIsAddingToCart] = useState(false);
  const [quantity, setQuantity] = useState(1);


  // Format product ID if needed
  const formattedProductId = React.useMemo(() => {
    if (!productId) return null;
    
    // If already a GID, use as is
    if (productId.startsWith("gid://")) {
      return productId;
    }
    
    // If it's a numeric ID, format it as GID
    // Remove any whitespace or special characters
    const cleanId = productId.toString().trim();
    
    // Check if it's already a full GID format
    if (cleanId.includes("shopify/Product/")) {
      return cleanId.startsWith("gid://") ? cleanId : `gid://${cleanId}`;
    }
    
    // Format as GID
    return `gid://shopify/Product/${cleanId}`;
  }, [productId]);

  // Initialize Shopify client
  const getClient = () => {
    // Make sure domain is properly formatted
    let formattedDomain = domain;
    if (!formattedDomain.startsWith("http")) {
      formattedDomain = `https://${formattedDomain}`;
    }

    try {
      return createStorefrontApiClient({
        storeDomain: formattedDomain,
        apiVersion: "2025-10",
        publicAccessToken: storefrontAccessToken,
      });
    } catch (error) {
      toast.error(
        "Failed to initialize Shopify client. Check your domain and access token."
      );
      return null;
    }
  };

  // Fetch product data
  useEffect(() => {
    const fetchProduct = async () => {
      setIsLoading(true);

      try {
        const client = getClient();
        if (!client) {
          throw new Error("Failed to initialize Shopify client");
        }

        let query = "";
        let variables = {};

        if (productHandle) {
          query = `
            query GetProductByHandle($handle: String!) {
              product(handle: $handle) {
                id
                title
                variants(first: 10) {
                  edges {
                    node {
                      id
                      title
                      price {
                        amount
                        currencyCode
                      }
                      availableForSale
                    }
                  }
                }
              }
            }
          `;
          variables = { handle: productHandle };
        } else if (formattedProductId) {
          query = `
            query GetProductById($id: ID!) {
              product(id: $id) {
                id
                title
                variants(first: 10) {
                  edges {
                    node {
                      id
                      title
                      price {
                        amount
                        currencyCode
                      }
                      availableForSale
                    }
                  }
                }
              }
            }
          `;
          variables = { id: formattedProductId };
        } else {
          throw new Error("Either productId or productHandle must be provided");
        }

        const { data, errors } = await client.request(query, {
          variables,
        });

        if (errors) {
          const errorMessage = Array.isArray(errors)
            ? errors[0]?.message
            : (errors as any)?.message || JSON.stringify(errors);
          throw new Error(`Failed to fetch product: ${errorMessage}`);
        }

        if (data?.product) {
          setProduct(data.product);

          // Set the selected variant
          const variants = data.product.variants.edges.map(
            (edge: any) => edge.node
          );

          let selectedVariantToSet = null;
          if (variantId) {
            selectedVariantToSet = variants.find((v: any) => v.id === variantId);
          }
          if (!selectedVariantToSet) {
            selectedVariantToSet = variants[0];
          }

          setSelectedVariant(selectedVariantToSet);
        } else {
          throw new Error("Product not found");
        }
      } catch (error: any) {
        toast.error(error.message || "Failed to load product information");
      } finally {
        setIsLoading(false);
      }
    };

    if (
      domain &&
      storefrontAccessToken &&
      (formattedProductId || productHandle)
    ) {
      fetchProduct();
    } else {
      setIsLoading(false);
    }
  }, [
    domain,
    storefrontAccessToken,
    formattedProductId,
    productHandle,
    variantId,
  ]);

  // Create or retrieve cart
  const getCart = async () => {
    try {
      const client = getClient();
      if (!client) {
        throw new Error("Failed to initialize Shopify client");
      }

      // Check if we already have a cart ID in localStorage
      const existingCartId = localStorage.getItem("shopifyCartId");

      if (existingCartId) {
        // Verify the cart still exists
        const checkCartQuery = `
          query GetCart($cartId: ID!) {
            cart(id: $cartId) {
              id
            }
          }
        `;

        const { data, errors } = await client.request(checkCartQuery, {
          variables: { cartId: existingCartId },
        });

        if (!errors && data?.cart) {
          return existingCartId;
        }
      }

      // Create a new cart
      const createCartMutation = `
        mutation CreateCart {
          cartCreate {
            cart {
              id
              checkoutUrl
            }
            userErrors {
              field
              message
            }
          }
        }
      `;

      const { data, errors } = await client.request(createCartMutation);

      if (errors || data?.cartCreate?.userErrors?.length > 0) {
        throw new Error("Failed to create cart");
      }

      const newCartId = data?.cartCreate?.cart?.id;

      if (newCartId) {
        localStorage.setItem("shopifyCartId", newCartId);
        return newCartId;
      }

      throw new Error("Failed to create cart");
    } catch (error) {
      throw error;
    }
  };

  // Add to cart function
  const addToCart = async () => {
    if (!selectedVariant) {
      return;
    }

    // Check if variant is available for sale
    if (selectedVariant.availableForSale === false) {
      toast.error(t("outOfStockMessage"));
      return;
    }

    setIsAddingToCart(true);

    try {

      const cartId = await getCart();
      const client = getClient();
      if (!client) {
        throw new Error("Failed to initialize Shopify client");
      }

      const addToCartMutation = `
        mutation AddToCart($cartId: ID!, $lines: [CartLineInput!]!) {
          cartLinesAdd(cartId: $cartId, lines: $lines) {
            cart {
              id
              checkoutUrl
              totalQuantity
            }
            userErrors {
              field
              message
            }
          }
        }
      `;

      const variables = {
        cartId,
        lines: [
          {
            quantity: quantity,
            merchandiseId: selectedVariant.id,
          },
        ],
      };

      const { data, errors } = await client.request(addToCartMutation, {
        variables,
      });

      if (errors) {
        const errorMessage = Array.isArray(errors)
          ? errors[0]?.message
          : (errors as any)?.message || JSON.stringify(errors);
        throw new Error(`Failed to add item to cart: ${errorMessage}`);
      }

      if (data?.cartLinesAdd?.userErrors?.length > 0) {
        throw new Error(`Failed to add item to cart: ${data.cartLinesAdd.userErrors[0]?.message || "Unknown error"}`);
      }

      const unitPrice = Number.parseFloat(selectedVariant.price.amount);
      trackAddToCart(
        {
          currency: selectedVariant.price.currencyCode,
          value: unitPrice * quantity,
          items: [
            {
              item_id: selectedVariant.id,
              item_name: product.title,
              item_brand: brand,
              price: unitPrice,
              quantity,
            },
          ],
        },
        {
          cart_id: data.cartLinesAdd.cart?.id ?? cartId,
          cart_total_quantity: data.cartLinesAdd.cart?.totalQuantity,
        },
      );

      // Success!
      toast.success(
        `${product.title} (${quantity}) has been added to your cart.`
      );

      // Dispatch a custom event to notify the mini cart
      window.dispatchEvent(new CustomEvent("shopify:cart:updated"));
    } catch (error) {
      toast.error("Failed to add item to cart");
    } finally {
      setIsAddingToCart(false);
    }
  };

  if (isLoading) {
    return (
      <Card>
        <CardContent className="p-4 space-y-4">
          <Skeleton className="h-6 w-1/3" />
          <Skeleton className="h-10 w-full" />
        </CardContent>
        <CardFooter>
          <Skeleton className="h-10 w-full" />
        </CardFooter>
      </Card>
    );
  }

  if (!product || !selectedVariant) {
    return (
      <Card>
        <CardContent className="p-4">
          <p className="text-center text-muted-foreground">Product not found</p>
        </CardContent>
      </Card>
    );
  }

  const formatPrice = (amount: string, currencyCode: string) => {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: currencyCode,
    }).format(Number.parseFloat(amount));
  };

  const price = formatPrice(
    selectedVariant.price.amount,
    selectedVariant.price.currencyCode
  );
  const totalPrice = formatPrice(
    (Number.parseFloat(selectedVariant.price.amount) * quantity).toString(),
    selectedVariant.price.currencyCode
  );

  const swapPalette = shouldSwapBuyButtonForSurface(
    surfaceBackgroundHex,
    color,
    cta
  );
  const buttonColor = swapPalette ? cta : color;
  const buttonCta = swapPalette ? color : cta;
  const buttonCustomColor = swapPalette ? cta : customColor;

  const style = {
    "--highlightColor": config?.highlightColor?.color.hex,
    "--color": config?.textColor.color.hex,
    "--bgColor": cart?.backgroundColor.color.hex,
  } as React.CSSProperties;

  return (
    <Card className={disableQuantity ? "flex justify-center items-stretch h-full" : "flex flex-row-reverse  justify-between gap-1 items-stretch h-full"}>
      {!disableQuantity && (
        <CardContent className="h-full">
          <div
            style={style}
            className={`flex items-center gap-2 h-full ${styles.selectContent}`}
          >
            <Select
              value={quantity.toString()}
              onValueChange={(value: any) => setQuantity(Number.parseInt(value))}
            >
              <SelectTrigger id="quantity">
                <SelectValue placeholder="1" />
              </SelectTrigger>

              <SelectContent>
                {Array.from({ length: maxQuantity }, (_, i) => i + 1).map(
                  (num) => (
                    <SelectItem
                      style={style}
                      className={` ${styles.selectContent}`}
                      key={num}
                      value={num.toString()}
                    >
                      <MultiPurposeParagraph
                        type="default"
                        data={`${num}`}
                        color={cart?.backgroundColor.color}
                      />
                    </SelectItem>
                  )
                )}
              </SelectContent>
            </Select>

            {/*    {quantity > 1 && (
              <p className="text-sm text-muted-foreground">Total: {totalPrice}</p>
            )} */}
          </div>
        </CardContent>
      )}
      <CardFooter className={cardFooterClassName}>
        <div className={selectedVariant?.availableForSale === false ? 'opacity-50 pointer-events-none' : ''}>
          <ButtonMaster
            onClick={selectedVariant?.availableForSale === false ? undefined : addToCart}
            anim={"slideLeft"}
            type={type}
            color={buttonColor}
            cta={buttonCta}
            customColor={buttonCustomColor}
          >
            {isLoading ? (
              <Skeleton className="h-4 w-[100px]" />
            ) : isAddingToCart ? (
              "Adding..."
            ) : selectedVariant?.availableForSale === false ? (
              t("outOfStock")
            ) : (
              <>
                {" "}
                <p className="">
                  {" "}
                  {label} {disableQuantity || quantity === 1 ? `${price}` : `${totalPrice}`}{" "}
                </p>
              </>
            )}
          </ButtonMaster>
        </div>
      </CardFooter>
    </Card>
  );
}
